import { useNavigate, useSearchParams } from "react-router-dom";
import CloseIcon from "./../../assets/icons/close.svg?react";
import CheckIcon from "./../..//assets/icons/check.svg?react";
import { AiNotebooksUrl } from "../../utils/urls";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AiNotebookCreateSchema } from "../../utils/validations";
import {
  selectInstancesRegions,
  selectInstancesRegionsLoading,
  getRegionsAsync,
} from "../../store/instances/instancesSlice";
import {
  getDBSCatalogsAsync,
  selectDBSCatalogs,
  selectDBSCatalogsLoading,
} from "../../store/database/servicesSlice";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { Button, cn, Flex, Loading, Typography } from "djuno-design";
import useLimitations from "../../hooks/useLimitations";
import { DBSCatalog } from "../../types/database";
import {
  createAiNotebookAsync,
  getAiNotebookEditorsAsync,
  getAiNotebookFreamworksAsync,
  getAiRegionsDataAsync,
  getAiResourceFlavorsAsync,
  selectAiNotebookEditors,
  selectAiNotebookEditorsLoading,
  selectAiNotebookFreamworks,
  selectAiNotebookFreamworksLoading,
  selectAiNotebooks,
  selectAiNotebooksActionLoading,
  // selectAiNotebooksLoading,
  selectAiRegionsData,
  selectAiRegionsDataLoading,
  selectAiResourceFlavors,
} from "../../store/ai/notebooks/notebooksSlice";
import {
  AiServiceAttachRepoInput,
  AiServiceAttachSshInput,
  AiServiceConfigInput,
  AiServiceEditorInput,
  AiServiceFlavorInput,
  AiServiceFreamworkInput,
  AiServiceRegionInput,
} from "../../components/ai-notebooks/create/components";
import { getInstancesSshKeysAsync } from "../../store/instances/instancesSSHkeysSlice";
import { datacenterDetails } from "../../utils/countries";
import { AiResourceUseCaseType } from "../../types/ai";
import { currency } from "../../utils/currency";
import { useScrollToElement } from "../../hooks/useScrollToElement";

type CreateStep =
  | "configuration"
  | "codeEditor"
  | "freamwork"
  | "region"
  | "resources"
  | "attachContainers"
  | "attachSSH"
  | "review";

const createSteps: Array<CreateStep> = [
  "configuration",
  "codeEditor",
  "freamwork",
  "region",
  "resources",
  "attachContainers",
  "attachSSH",
  "review",
];

export type CreateNotebookFormType = {
  serviceName: string;
  labels?: Array<{ key: string; value: string }>;
  selectedEditor: string;
  selectedFreamwork: string;
  selectedFreamworkVersion: string;
  selectedRegionDataId: string;
  selectedResourceId: string;
  selectedResourceUnit: number;
  gitRepos?: Array<{ url: string; mountPath: string; permission: string }>;
  sshKeys?: Array<{ name: string; content: string; disabled: boolean }>;
};

const AiNotebookCreatePage = () => {
  const [query] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const notebooks = useAppSelector(selectAiNotebooks);
  // const notebooksLoading = useAppSelector(selectAiNotebooksLoading);

  const editors = useAppSelector(selectAiNotebookEditors);
  const editorsLoading = useAppSelector(selectAiNotebookEditorsLoading);

  const freamworks = useAppSelector(selectAiNotebookFreamworks);
  const freamworksLoading = useAppSelector(selectAiNotebookFreamworksLoading);

  const regions = useAppSelector(selectInstancesRegions);
  const regionsLoading = useAppSelector(selectInstancesRegionsLoading);
  const regionsData = useAppSelector(selectAiRegionsData);
  const regionsDataLoading = useAppSelector(selectAiRegionsDataLoading);

  const flavors = useAppSelector(selectAiResourceFlavors);

  const catalogs = useAppSelector(selectDBSCatalogs);
  const catalogsLoading = useAppSelector(selectDBSCatalogsLoading);

  const actionLoading = useAppSelector(selectAiNotebooksActionLoading);

  // states
  const [expanded, setExpanded] = useState<CreateStep>("configuration");
  const [selectedUseCase, setSelectedUseCase] =
    useState<AiResourceUseCaseType>("cpu");
  const [filteredCatalogs, setFilteredCatalogs] = useState<Array<DBSCatalog>>(
    []
  );

  //data for create from backup
  const returnUrl = query.get("returnUrl");

  const methods = useForm<CreateNotebookFormType>({
    resolver: yupResolver(AiNotebookCreateSchema),
    mode: "all",
    defaultValues: {
      serviceName: "",
      labels: undefined,
      selectedEditor: "",
      selectedFreamwork: "",
      selectedFreamworkVersion: "",
      selectedRegionDataId: "",
      selectedResourceId: "",
      selectedResourceUnit: 1,
      gitRepos: undefined,
      sshKeys: undefined,
    },
  });

  const {
    setValue,
    handleSubmit,
    control,
    formState: { errors, isValid, isValidating },
    trigger,
    watch,
    register,
  } = methods;

  // watch form values
  const notebookName = watch("serviceName");
  const notebookLabels = watch("labels");
  const selectedEditorId = watch("selectedEditor");
  const selectedFreamworkId = watch("selectedFreamwork");
  const selectedFreamworkVersionName = watch("selectedFreamworkVersion");
  const selectedRegionDataId = watch("selectedRegionDataId");
  const selectedResourceId = watch("selectedResourceId");
  const selectedResourceUnit = watch("selectedResourceUnit");
  const attachedGitRepos = watch("gitRepos");
  const attachedSshKeys = watch("sshKeys");

  useEffect(() => {
    trigger("labels");
  }, [notebookLabels, trigger, isValidating]);

  useEffect(() => {
    trigger("gitRepos");
  }, [attachedGitRepos, trigger]);

  useEffect(() => {
    trigger("sshKeys");
  }, [attachedSshKeys, trigger]);

  //get first data
  useEffect(() => {
    dispatch(getAiNotebookEditorsAsync({}));
    dispatch(getAiNotebookFreamworksAsync({}));
    dispatch(getRegionsAsync());
    dispatch(getAiRegionsDataAsync({}));
    dispatch(getInstancesSshKeysAsync());
    dispatch(getDBSCatalogsAsync("ai-notebook"));
  }, [dispatch]);

  // get resource (flavors) based on selected region
  useEffect(() => {
    if (selectedRegionDataId && selectedRegionDataId !== "")
      dispatch(getAiResourceFlavorsAsync({ region: selectedRegionDataId }));
  }, [dispatch, selectedRegionDataId]);

  // find selected code editor data
  const selectedCodeEditor = useMemo(() => {
    const _selectedImage = editors.find((e) => e.id === selectedEditorId);
    return _selectedImage;
  }, [editors, selectedEditorId]);

  // find selected freamwork data
  const selectedFreamwork = useMemo(() => {
    const _selectedImage = freamworks.find((f) => f.id === selectedFreamworkId);
    return _selectedImage;
  }, [freamworks, selectedFreamworkId]);

  // find selected region data
  const selectedRegion = useMemo(() => {
    return regions.find((r) => r.datacenterLocation === selectedRegionDataId);
  }, [regions, selectedRegionDataId]);

  // find selected datacenter
  const selectedDatacenter = useMemo(() => {
    return datacenterDetails.find((dc) => dc.location === selectedRegionDataId);
  }, [selectedRegionDataId]);

  // find selected freamwork data
  const selectedResource = useMemo(() => {
    return flavors.find((f) => f.id === selectedResourceId);
  }, [flavors, selectedResourceId]);

  // filter addons by selected engine and period
  useEffect(() => {
    if (catalogs) {
      const filtered = catalogs.filter((catalog) =>
        catalog.product?.startsWith("publiccloud-ai-notebook")
      );
      setFilteredCatalogs(filtered);
    }
  }, [catalogs]);

  const catalog = useMemo(() => {
    return filteredCatalogs.find((c) =>
      c.planCode.startsWith(
        "ai-notebook." + selectedResourceId + ".minute.consumption"
      )
    );
  }, [filteredCatalogs, selectedResourceId]);

  const handleEditConfigSelection = () => {
    setExpanded("configuration");
  };

  const handleEditCodeEditorSelection = () => {
    setExpanded("codeEditor");
  };

  const handleEditFreamworkSelection = () => {
    setExpanded("freamwork");
  };

  const handleEditRegionSelection = () => {
    setExpanded("region");
  };

  const handleEditResourcesSelection = () => {
    setExpanded("resources");
  };

  const handleEditAttachContainersSelection = () => {
    setExpanded("attachContainers");
  };

  const handleEditAttachSshSelection = () => {
    setExpanded("attachSSH");
  };

  const handleSubmitForm = () => {
    // console.log(data);

    if (!selectedResource) return;

    const labels: Record<string, string> = (notebookLabels || [])?.reduce(
      (acc, { key, value }) => {
        acc[key] = value;
        return acc;
      },
      {} as Record<string, string>
    );

    const volumes = (attachedGitRepos || []).map((repo) => ({
      mountPath: repo.mountPath,
      permission: repo.permission,
      publicGit: {
        url: repo.url,
      },
    }));

    const sshPublicKeys = (attachedSshKeys || []).map((key) => key.content);

    // calc resource data
    let resources: any = {
      flavor: selectedResource.id,
      memory: selectedResource.resourcesPerUnit.memory,
      publicNetwork: selectedResource.resourcesPerUnit.publicNetwork,
      privateNetwork: 0,
      ephemeralStorage: selectedResource.resourcesPerUnit.ephemeralStorage,
    };
    //for cpu types
    if (selectedUseCase === "cpu")
      resources = {
        ...resources,
        cpu: selectedResourceUnit,
      };

    //for gpu types
    if (selectedUseCase === "gpu")
      resources = {
        ...resources,
        gpu: selectedResourceUnit,
        gpuBrand: selectedResource.gpuInformation?.gpuBrand,
        gpuModel: selectedResource.gpuInformation?.gpuModel,
        gpuMemory: selectedResource.gpuInformation?.gpuMemory,
      };

    const apiData = {
      env: {
        editorId: selectedEditorId,
        frameworkId: selectedFreamworkId,
        frameworkVersion: selectedFreamworkVersionName,
      },
      labels,
      name: notebookName,
      region: selectedRegionDataId,
      resources,
      volumes,
      unsecureHttp: false,
      sshPublicKeys,
    };

    // console.log(apiData);
    dispatch(createAiNotebookAsync({ data: apiData })).then((action) => {
      if (action.type === "ai-notebooks/create/fulfilled") {
        navigate(returnUrl || AiNotebooksUrl);
      }
    });
  };

  const { numberOfAiNotebooksLimit, numberOfAiNotebooksLimitChecker } =
    useLimitations({
      cases: ["NumberOfAiNotebooks"],
    });

  const isExceedNumberOfAiNotebooks = useMemo(() => {
    return numberOfAiNotebooksLimitChecker(notebooks.length);
  }, [notebooks, numberOfAiNotebooksLimitChecker]);

  const codeEditorRef = useRef<HTMLDivElement>(null);

  useScrollToElement({ targetElementId: expanded });

  return (
    <>
      <div className="flex items-center justify-between h-16 px-6 sticky top-0 z-20 bg-white dark:bg-dark-1 border-b dark:border-dark-2">
        <div className="items-center justify-between flex flex-1 transition duration-150">
          <Typography.Text className="font-medium">
            Create a notebook
          </Typography.Text>
        </div>
        <div className="">
          <Button
            uiType="light"
            uiSize="small"
            onClick={() => navigate(returnUrl || AiNotebooksUrl)}
            className="group"
          >
            <CloseIcon className="w-3 h-3 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
          </Button>
        </div>
      </div>

      {catalogsLoading && (
        <Flex
          items="center"
          justify="center"
          className="h-full w-full min-h-[calc(100vh-128px)]"
        >
          <Loading borderSize={2} />
        </Flex>
      )}
      {!catalogsLoading && (
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <div className="mt-10 w-full px-6 flex flex-col lg:flex-row pb-24 gap-8 relative">
            <div className="w-full flex flex-col">
              {/* select configs */}
              <Accordion
                expanded={expanded === "configuration"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="configuration"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 0 ? (
                      <div className="flex flex-col gap-5 w-full">
                        <Flex
                          direction={{ default: "col", md: "row" }}
                          justify={{ default: "between" }}
                          className="gap-4 w-full"
                        >
                          <Typography.Text className="!text-base font-medium flex items-center gap-1">
                            <CheckIcon className="w-5 h-5 text-primary-500" />
                            Notebook name: {notebookName}{" "}
                            <Typography.Text size="xs" uiType="secondary">
                              ({notebookLabels?.length || 0} labels)
                            </Typography.Text>
                          </Typography.Text>
                          <Typography.Link
                            className="!text-sm"
                            onClick={handleEditConfigSelection}
                          >
                            edit this step
                          </Typography.Link>
                        </Flex>
                      </div>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "configuration",
                          }
                        )}
                      >
                        1. Configure your notebook
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <AiServiceConfigInput
                      control={control}
                      register={register}
                      errors={errors}
                      serviceNameInputLabel="Notebook Name"
                    />
                  </div>

                  <Button
                    disabled={
                      !notebookName ||
                      notebookName === "" ||
                      errors.serviceName !== undefined ||
                      "labels" in errors
                    }
                    // onClick={() => setExpanded("codeEditor")}
                    onClick={() => {
                      setExpanded("codeEditor");
                    }}
                    id="4th-next-button"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* select code editor */}
              <Accordion
                expanded={expanded === "codeEditor"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="codeEditor"
                  sx={{ my: 0 }}
                >
                  <div className="flex w-full items-center justify-between">
                    {createSteps.indexOf(expanded) > 1 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 flex-shrink-0 text-primary-500" />
                          Code editor chosen: {selectedCodeEditor?.name}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditCodeEditorSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <div ref={codeEditorRef}>
                        <Typography.Text
                          className={cn(
                            "text-base font-medium flex items-center gap-1",
                            {
                              "!text-primary-500": expanded === "codeEditor",
                            }
                          )}
                        >
                          2. Code editors
                        </Typography.Text>
                      </div>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3">
                    <AiServiceEditorInput
                      control={control}
                      errorMessage={errors.selectedEditor?.message}
                      editors={editors}
                      loading={editorsLoading}
                    />
                  </div>
                  <Button
                    id="1th-next-button"
                    disabled={selectedEditorId === ""}
                    onClick={() => setExpanded("freamwork")}
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* select freamwork */}
              <Accordion
                expanded={expanded === "freamwork"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="freamwork"
                  sx={{ my: 0 }}
                >
                  <div className="flex w-full items-center justify-between">
                    {createSteps.indexOf(expanded) > 2 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 flex-shrink-0 text-primary-500" />
                          Framework selected: {selectedFreamwork?.name} -{" "}
                          {selectedFreamworkVersionName}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditFreamworkSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "freamwork",
                          }
                        )}
                      >
                        3. Available frameworks
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3">
                    <AiServiceFreamworkInput
                      control={control}
                      freamworks={freamworks}
                      errorMessage={
                        errors.selectedFreamwork?.message ||
                        errors.selectedFreamworkVersion?.message
                      }
                      loading={freamworksLoading}
                    />
                  </div>
                  <Button
                    id="1th-next-button"
                    disabled={
                      selectedFreamworkId === "" ||
                      selectedFreamworkVersionName === ""
                    }
                    onClick={() => setExpanded("region")}
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* select region */}
              <Accordion
                expanded={expanded === "region"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="region"
                  sx={{ my: 0 }}
                >
                  <div className="flex w-full items-center justify-between">
                    {createSteps.indexOf(expanded) > 3 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          Location selected: {selectedRegionDataId}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditRegionSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "region",
                          }
                        )}
                      >
                        4. Datacenter location
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3">
                    <AiServiceRegionInput
                      regions={regions}
                      regionsData={regionsData}
                      control={control}
                      loading={regionsLoading || regionsDataLoading}
                      errorMessage={errors.selectedRegionDataId?.message}
                      // disabled={!!instanceSnapshot}
                    />
                  </div>
                  <Button
                    id="2th-next-button"
                    disabled={selectedRegionDataId === ""}
                    onClick={() => setExpanded("resources")}
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* select resources */}
              <Accordion
                expanded={expanded === "resources"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="resources"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 4 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          Resource selected: {selectedResourceId} - x
                          {selectedResourceUnit}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditResourcesSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "resources",
                          }
                        )}
                      >
                        5. Resources
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <AiServiceFlavorInput
                      control={control}
                      selectedUseCase={selectedUseCase}
                      setSelectedUseCase={setSelectedUseCase}
                      catalogs={filteredCatalogs}
                      catalogsLoading={catalogsLoading}
                      errorMessage={errors.selectedResourceId?.message}
                      useCasePrefix="AI Notebooks"
                    />
                  </div>

                  <Button
                    disabled={!selectedResourceId || selectedResourceId === ""}
                    onClick={() => setExpanded("attachContainers")}
                    id="3th-next-button"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* attach Containers */}
              <Accordion
                expanded={expanded === "attachContainers"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="attachContainers"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 5 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          Containers attached: {attachedGitRepos?.length ||
                            0}{" "}
                          volumes attached
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditAttachContainersSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500":
                              expanded === "attachContainers",
                          }
                        )}
                      >
                        6. Attach a container or a Git repository
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <AiServiceAttachRepoInput
                      control={control}
                      register={register}
                      errors={errors}
                    />
                  </div>

                  <Button
                    disabled={
                      errors.gitRepos &&
                      errors.gitRepos.length !== undefined &&
                      errors.gitRepos.length > 0
                    }
                    onClick={() => setExpanded("attachSSH")}
                    id="attachContainers"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* attach SSH */}
              <Accordion
                expanded={expanded === "attachSSH"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="attachSSH"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 6 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          Attached SSH public keys:{" "}
                          {attachedSshKeys?.length || 0}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditAttachSshSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "attachSSH",
                          }
                        )}
                      >
                        7. Attach SSH public keys
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <AiServiceAttachSshInput
                      control={control}
                      setValue={setValue}
                      errors={errors}
                    />
                  </div>

                  <Button
                    disabled={
                      errors.sshKeys &&
                      errors.sshKeys.length !== undefined &&
                      errors.sshKeys.length > 0
                    }
                    onClick={() => setExpanded("review")}
                    id="attachSSH"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* review */}
              <Accordion
                expanded={expanded === "review"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="review"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 7 ? (
                      <div className="flex flex-col gap-5 w-full">
                        <div className="flex w-full items-center justify-between">
                          <Typography.Text className="!text-base font-medium flex items-center gap-1">
                            <CheckIcon className="w-5 h-5 text-primary-500" />
                            Review
                          </Typography.Text>
                        </div>
                      </div>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "review",
                          }
                        )}
                      >
                        8. Review
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <Flex direction="col" className="w-full gap-5 px-5">
                      <Flex direction="col" className="w-full">
                        <Typography.Text size="sm">Code editor</Typography.Text>
                        <Typography.Text size="sm" uiType="secondary">
                          {selectedCodeEditor?.name}
                        </Typography.Text>
                      </Flex>

                      <Flex direction="col" className="w-full">
                        <Typography.Text size="sm">Framework</Typography.Text>
                        <Typography.Text size="sm" uiType="secondary">
                          {selectedFreamwork?.name} -{" "}
                          {selectedFreamworkVersionName}
                        </Typography.Text>
                      </Flex>

                      <Flex direction="col" className="w-full">
                        <Typography.Text size="sm">Region</Typography.Text>
                        <Typography.Text size="sm" uiType="secondary">
                          {selectedRegion ? (
                            <>
                              {selectedRegion.countryCode.toUpperCase()} -{" "}
                              {selectedDatacenter?.title} ({selectedRegion.name}
                              )
                            </>
                          ) : (
                            <> {selectedRegionDataId}</>
                          )}
                        </Typography.Text>
                      </Flex>

                      {catalog && (
                        <Flex direction="col" className="w-full">
                          <Typography.Text size="sm">Price</Typography.Text>
                          <Typography.Text size="sm" uiType="secondary">
                            {currency(
                              selectedResourceUnit *
                                catalog.pricings[0].price *
                                60
                            )}{" "}
                            resource/hour
                          </Typography.Text>
                        </Flex>
                      )}

                      <Flex direction="col" className="w-full">
                        <Typography.Text size="sm">
                          Template chosen
                        </Typography.Text>
                        <Typography.Text size="sm" uiType="secondary">
                          {selectedResourceId} - x{selectedResourceUnit}
                        </Typography.Text>
                      </Flex>
                    </Flex>
                  </div>

                  <Flex>
                    <Button
                      type="submit"
                      disabled={
                        !isValid || isExceedNumberOfAiNotebooks || actionLoading
                      }
                      uiType="primary"
                      loading={actionLoading}
                      tooltip={{
                        content: isExceedNumberOfAiNotebooks
                          ? numberOfAiNotebooksLimit?.LimitationMessage
                          : "",
                        className: "!text-sm",
                      }}
                    >
                      Create a Notebook
                    </Button>
                  </Flex>
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
        </form>
      )}
      {/* <InstancesSskKeyEditorModal /> */}
    </>
  );
};

export default AiNotebookCreatePage;
